import dayjs from 'dayjs';
import { ICopyWithBusinessRef } from '../../contracts/copy-with-business-ref.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { CalendarEntry } from '../calendar-entry.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { SerializationContext, DataModelBuilder, SerializedData, isSameData } from '../dto/dto.js';
import { NotificationEvent, SyncEventSource } from '../sync-event.js';
import { BusinessModuleRef } from './business-module-ref.js';

export class CancelNotificationEvent extends NotificationEvent implements ICopyWithBusinessRef {
  static readonly TYPE_ID: string = '0355106afff54c0cb9a1bea6cf0e7263';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly source: SyncEventSource,
    public readonly seen: boolean,
    public readonly entry: CalendarEntry,
    public readonly userId: string,
  ) {
    super();
  }

  get $typeId(): string {
    return CancelNotificationEvent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, CancelNotificationEvent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): CancelNotificationEvent {
    return new CancelNotificationEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        MapUtils.get<boolean>(map, 'seen'),
        context.fromData<CalendarEntry>(MapUtils.get<SerializedData>(map, 'entry')),
        MapUtils.get<string>(map, 'userId'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'seen': this.seen,
      'entry': context.toData(this.entry),
      'userId': this.userId,
    };
  }

  clone(): this {
    return new CancelNotificationEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.seen,
        this.entry.clone(),
        this.userId,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        other.seen === this.seen &&
        isSameData(other.entry, this.entry) &&
        other.userId === this.userId;
  }

  markAsSeen(): this {
    return new CancelNotificationEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        true,
        this.entry.clone(),
        this.userId,
    ) as this;
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new CancelNotificationEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        true,
        this.entry.copyWithBusinessRef(businessRef),
        this.userId,
    ) as this;
  }
}

export class CancelNotificationEventBuilder extends DataModelBuilder<CancelNotificationEvent> {
  build(context: SerializationContext, map: SerializedData): CancelNotificationEvent {
    return CancelNotificationEvent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(CancelNotificationEvent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(CancelNotificationEvent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - add `deviceId` of type: string | null
     */
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
